/**
 * Request Manager
 *
 * Gestisce la cancellazione centralizzata delle richieste HTTP pending.
 * Si integra con l'interceptor axios per registrare le chiamate marcate con cancelOnRouteChange: true
 * e permette di cancellarle al cambio di rotta.
 *
 * APPROCCIO OPT-IN: solo le richieste esplicitamente marcate vengono cancellate,
 * per evitare regressioni sul comportamento esistente.
 *
 * @version 1.0.0
 * @since 22-Jan-2026
 */

// Map che tiene traccia di tutti gli AbortController attivi
// key: requestId univoco, value: { controller, url, timestamp }
const pendingRequests = new Map()
let requestCounter = 0

/**
 * Genera un ID univoco per ogni richiesta
 * @returns {string} ID univoco della richiesta
 */
const generateRequestId = () => `req_${++requestCounter}_${Date.now()}`

/**
 * Registra una nuova richiesta e restituisce il controller per la cancellazione
 * @param {string} url - URL della richiesta (per debug)
 * @returns {{ signal: AbortSignal, requestId: string }} Signal da passare ad axios e requestId
 */
export const register = (url) => {
  const requestId = generateRequestId()
  const controller = new AbortController()

  pendingRequests.set(requestId, {
    controller,
    url,
    timestamp: Date.now()
  })

  return {
    signal: controller.signal,
    requestId
  }
}

/**
 * Rimuove una richiesta dal registro (chiamata quando completa o fallisce)
 * @param {string} requestId - ID della richiesta da rimuovere
 */
export const unregister = (requestId) => {
  if (requestId && pendingRequests.has(requestId)) {
    pendingRequests.delete(requestId)
  }
}

/**
 * Cancella tutte le richieste pending registrate
 * Da chiamare al cambio di rotta
 */
export const cancelAllPending = () => {
  let cancelledCount = 0

  pendingRequests.forEach((request, requestId) => {
    try {
      request.controller.abort()
      cancelledCount++
    } catch (e) {
      // Controller già abortito, ignora
    }
    pendingRequests.delete(requestId)
  })

  if (process.env.DEBUG_MODE === 'true' && cancelledCount > 0) {
    console.log(`[RequestManager] Cancelled ${cancelledCount} pending requests on route change`)
  }
}

/**
 * Cancella una singola richiesta specifica
 * @param {string} requestId - ID della richiesta da cancellare
 */
export const cancel = (requestId) => {
  const request = pendingRequests.get(requestId)
  if (request) {
    try {
      request.controller.abort()
    } catch (e) {
      // Controller già abortito, ignora
    }
    pendingRequests.delete(requestId)
  }
}

/**
 * Verifica se una richiesta è stata cancellata
 * @param {Error} error - Errore da verificare
 * @returns {boolean} True se l'errore è dovuto a cancellazione
 */
export const isAbortError = (error) => (
  error?.name === 'AbortError' ||
  error?.code === 'ERR_CANCELED' ||
  error?.message === 'canceled'
)

/**
 * Restituisce il numero di richieste pending
 * @returns {number} Numero di richieste in corso
 */
export const getPendingCount = () => pendingRequests.size

/**
 * Pulisce richieste "stale" (più vecchie di un certo timeout)
 * Utile per debug e pulizia memoria
 * @param {number} maxAge - Età massima in millisecondi (default 5 minuti)
 */
export const cleanupStale = (maxAge = 300000) => {
  const now = Date.now()
  pendingRequests.forEach((request, requestId) => {
    if (now - request.timestamp > maxAge) {
      cancel(requestId)
    }
  })
}

// Export default per retrocompatibilità
export default {
  register,
  unregister,
  cancelAllPending,
  cancel,
  isAbortError,
  getPendingCount,
  cleanupStale
}
